import posthog from 'posthog-js'

// Page view events
export const trackPageView = (pageName: string, additionalProperties?: Record<string, any>) => {
    // Filter out undefined values
    const filteredProperties = additionalProperties 
        ? Object.fromEntries(
            Object.entries(additionalProperties).filter(([_, value]) => value !== undefined)
          )
        : {}

    posthog.capture('page_viewed', {
        page_name: pageName,
        page_url: window.location.pathname,
        page_title: document.title,
        ...filteredProperties
    })
}

export const trackLoginPageView = () => {
    trackPageView('Login', { page_category: 'login', page_type: 'login' })
}

// Specific page view tracking functions
export const trackHomePageView = () => {
    trackPageView('Home Dashboard', { page_category: 'main', page_type: 'dashboard' })
}

export const trackBillingPageView = () => {
    trackPageView('Billing Plans', { page_category: 'account', page_type: 'billing' })
}

export const trackAnalyticsPageView = (surveyId?: string) => {
    trackPageView('Survey Analytics', { 
        page_category: 'analytics', 
        page_type: 'analytics',
        survey_id: surveyId 
    })
}

export const trackTemplatePageView = () => {
    trackPageView('Survey Templates', { page_category: 'templates', page_type: 'templates' })
}

export const trackSettingsPageView = () => {
    trackPageView('Settings', { page_category: 'account', page_type: 'settings' })
}

export const trackOnboardingPageView = () => {
    trackPageView('Onboarding', { page_category: 'onboarding', page_type: 'onboarding' })
}

export const trackBuilderPageView = (surveyId?: string) => {
    trackPageView('Survey Builder', { 
        page_category: 'builder', 
        page_type: 'builder',
        survey_id: surveyId 
    })
}

export const trackHelpCenterPageView = () => {
    trackPageView('Help Center', { page_category: 'support', page_type: 'help' })
}

export const trackExclusivePlansPageView = () => {
    trackPageView('Exclusive Plans', { page_category: 'account', page_type: 'exclusive_plans' })
}

// Button click events
export const trackButtonClick = (buttonName: string, additionalProperties?: Record<string, any>) => {
    posthog.capture('button_clicked', {
        button_name: buttonName,
        page_url: window.location.pathname,
        ...additionalProperties
    })
}

// Specific button click tracking functions
export const trackCreateSurveyClick = (method: 'modal' | 'scratch' | 'template') => {
    trackButtonClick('Create Survey', { 
        button_category: 'survey_creation',
        creation_method: method,
        button_location: 'main_navigation'
    })
}

export const trackCreateSurveyFromScratchClick = () => {
    trackButtonClick('Create Survey from Scratch', { 
        button_category: 'survey_creation',
        creation_method: 'scratch',
        button_location: 'survey_creation_flow'
    })
}

export const trackCreateSurveyFromTemplateClick = (templateName?: string) => {
    trackButtonClick('Create Survey from Template', { 
        button_category: 'survey_creation',
        creation_method: 'template',
        template_name: templateName,
        button_location: 'template_selection'
    })
}

export const trackPublishButtonClick = (surveyId?: string) => {
    trackButtonClick('Publish Survey', { 
        button_category: 'survey_actions',
        action_type: 'publish',
        survey_id: surveyId,
        button_location: 'survey_builder'
    })
}

export const trackUseTemplateClick = (templateName?: string) => {
    trackButtonClick('Use Template', { 
        button_category: 'template_actions',
        action_type: 'use_template',
        template_name: templateName,
        button_location: 'template_card'
    })
}

export const trackPreviewTemplateClick = (templateName?: string) => {
    trackButtonClick('Preview Template', { 
        button_category: 'template_actions',
        action_type: 'preview_template',
        template_name: templateName,
        button_location: 'template_card'
    })
}

export const trackHelpCenterClick = () => {
    trackButtonClick('Help Center', { 
        button_category: 'navigation',
        button_location: 'sidebar',
        destination: 'help_center'
    })
}

export const trackNavigationClick = (destination: string, fromPage?: string) => {
    trackButtonClick('Navigation', { 
        button_category: 'navigation',
        destination,
        from_page: fromPage || window.location.pathname,
        button_location: 'sidebar'
    })
}

// Survey creation events
export const trackSurveyCreation = (creationMethod: string, additionalProperties?: Record<string, any>) => {
    posthog.capture('survey_created', {
        creation_method: creationMethod,
        page_url: window.location.pathname,
        ...additionalProperties
    })
}

// Survey publishing events
export const trackSurveyPublish = (surveyId: string, additionalProperties?: Record<string, any>) => {
    posthog.capture('survey_published', {
        survey_id: surveyId,
        page_url: window.location.pathname,
        ...additionalProperties
    })
}

// Template events
export const trackTemplateAction = (action: string, templateName?: string, additionalProperties?: Record<string, any>) => {
    posthog.capture('template_action', {
        action,
        template_name: templateName,
        page_url: window.location.pathname,
        ...additionalProperties
    })
}

// Navigation events
export const trackNavigation = (destination: string, additionalProperties?: Record<string, any>) => {
    posthog.capture('navigation', {
        destination,
        from_page: window.location.pathname,
        ...additionalProperties
    })
}

// Feature usage events
export const trackFeatureUsage = (feature: string, additionalProperties?: Record<string, any>) => {
    posthog.capture('feature_used', {
        feature,
        page_url: window.location.pathname,
        ...additionalProperties
    })
}

// Error tracking
export const trackError = (errorType: string, errorMessage: string, additionalProperties?: Record<string, any>) => {
    posthog.capture('error_occurred', {
        error_type: errorType,
        error_message: errorMessage,
        page_url: window.location.pathname,
        ...additionalProperties
    })
}

// User identification
export const identifyUser = (userId: string, userProperties?: Record<string, any>) => {
    posthog.identify(userId, userProperties)
}

// Set user properties
export const setUserProperties = (properties: Record<string, any>) => {
    posthog.people.set(properties)
}

// Survey action tracking functions
export const trackMoveToDraftClick = (surveyId?: string, surveyName?: string) => {
    trackButtonClick('Move to Draft', { 
        button_category: 'survey_actions',
        action_type: 'move_to_draft',
        survey_id: surveyId,
        survey_name: surveyName,
        button_location: 'survey_listing'
    })
}

export const trackDuplicateSurveyClick = (surveyId?: string, surveyName?: string) => {
    trackButtonClick('Duplicate Survey', { 
        button_category: 'survey_actions',
        action_type: 'duplicate_survey',
        survey_id: surveyId,
        survey_name: surveyName,
        button_location: 'survey_listing'
    })
}

export const trackViewAnalyticsClick = (surveyId?: string, surveyName?: string) => {
    trackButtonClick('View Analytics', { 
        button_category: 'survey_actions',
        action_type: 'view_analytics',
        survey_id: surveyId,
        survey_name: surveyName,
        button_location: 'survey_listing'
    })
}

export const trackEditSurveyClick = (surveyId?: string, surveyName?: string) => {
    trackButtonClick('Edit Survey', { 
        button_category: 'survey_actions',
        action_type: 'edit_survey',
        survey_id: surveyId,
        survey_name: surveyName,
        button_location: 'survey_listing'
    })
}

// Survey action completion events
export const trackSurveyMovedToDraft = (surveyId: string, surveyName?: string) => {
    posthog.capture('survey_moved_to_draft', {
        survey_id: surveyId,
        survey_name: surveyName,
        page_url: window.location.pathname,
    })
}

export const trackSurveyDuplicated = (surveyId: string, surveyName?: string, newSurveyId?: string) => {
    posthog.capture('survey_duplicated', {
        survey_id: surveyId,
        survey_name: surveyName,
        new_survey_id: newSurveyId,
        page_url: window.location.pathname,
    })
} 